// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  Add as AddIcon,
  ArrowForward,
  DeleteOutlineRounded,
  EditRounded,
  ViewArrayOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { formatNumber } from '@/utilities/number';
import { Workflow } from '@/workflow/api/types';
import { deleteWorkflow, setWorkflowAsDefault } from '@/workflow/api/workflows';
import { WorkflowEditDialog } from '@/workflow/components/workflow-edit-dialog';
import { useWorkflows } from '@/workflow/hooks/use-workflows';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { useState } from 'react';

export function OrganizationWorkflowsCard() {
  const editDialog = useDisclosure();
  const addDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(
    null
  );
  const [workflowToDelete, setWorkflowToDelete] = useState<Workflow | null>(
    null
  );
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(
    null
  );
  const [showDeleteSupportSnackbar, setShowDeleteSupportSnackbar] =
    useState(false);
  const queryClient = useQueryClient();

  const {
    data: workflows = [],
    isLoading,
    error,
  } = useWorkflows({ withCount: true });

  const setDefaultMutation = useMutation({
    mutationFn: setWorkflowAsDefault,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });

  const deleteMutation = useMutation<void, AxiosError, string>({
    mutationFn: (workflowKey: string) => deleteWorkflow(workflowKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      deleteDialog.close();
      setWorkflowToDelete(null);
      setDeleteErrorMessage(null);
      setShowDeleteSupportSnackbar(false);
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const isHandledDeleteError =
        apiError.apiStatusCode ===
          ERROR_CODE_MAPPING.project_workflow_cannot_delete_last_workflow ||
        apiError.apiStatusCode ===
          ERROR_CODE_MAPPING.project_workflow_cannot_delete_in_use_workflow;

      if (isHandledDeleteError) {
        setDeleteErrorMessage(getUserFacingErrorMessage(apiError));
        setShowDeleteSupportSnackbar(false);
        void queryClient.invalidateQueries({ queryKey: ['workflows'] });
        return;
      }

      setDeleteErrorMessage(null);
      setShowDeleteSupportSnackbar(true);
    },
  });

  const getWorkflowUsageCount = (workflow: Workflow): number => {
    return workflow.stages.reduce((sum, stage) => {
      return sum + (stage.inUseCount ?? 0);
    }, 0);
  };
  const currentWorkflowToDelete = workflowToDelete
    ? (workflows.find((workflow) => workflow.id === workflowToDelete.id) ??
      null)
    : null;
  const canConfirmDeleteWorkflow =
    currentWorkflowToDelete !== null &&
    workflows.length > 1 &&
    getWorkflowUsageCount(currentWorkflowToDelete) === 0;

  const handleEdit = (workflow: Workflow) => {
    setSelectedWorkflow(workflow);
    editDialog.open();
  };

  const handleAdd = () => {
    setSelectedWorkflow(null);
    addDialog.open();
  };

  const handleSetDefault = (workflow: Workflow) => {
    setDefaultMutation.mutate(workflow.key);
  };

  const handleDelete = (workflow: Workflow) => {
    deleteMutation.reset();
    setDeleteErrorMessage(null);
    setShowDeleteSupportSnackbar(false);
    setWorkflowToDelete(workflow);
    deleteDialog.open();
  };

  const handleConfirmDelete = () => {
    if (currentWorkflowToDelete && canConfirmDeleteWorkflow) {
      setDeleteErrorMessage(null);
      setShowDeleteSupportSnackbar(false);
      deleteMutation.mutate(currentWorkflowToDelete.key);
    }
  };

  const handleCloseDeleteDialog = () => {
    if (deleteMutation.isPending) {
      return;
    }
    deleteDialog.close();
    deleteMutation.reset();
    setWorkflowToDelete(null);
    setDeleteErrorMessage(null);
    setShowDeleteSupportSnackbar(false);
  };

  if (error) {
    return (
      <Paper variant="outlined">
        <Box p={2}>
          <Typography color="error">
            Failed to load project workflows
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack
          useFlexGap
          spacing={1}
          paddingX={2}
          paddingY={2}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          bgcolor="grey.50"
        >
          <Typography variant="overline">Project workflows</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add workflow
          </Button>
        </Stack>
        <Divider />

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : workflows.length === 0 ? (
          <Box p={2}>
            <Typography variant="body2" color="text.secondary" align="center">
              No project workflows created yet
            </Typography>
          </Box>
        ) : (
          <Box p={2}>
            <Stack spacing={1}>
              {workflows.map((workflow) => (
                <Box
                  key={workflow.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'grey.25',
                    },
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 2,
                    }}
                  >
                    {/* Left container - Icon and workflow details */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        width: '20rem',
                        flex: '0 0 20rem',
                      }}
                    >
                      <Box
                        sx={(theme) => ({
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          border: `1px ${theme.palette.primary.main} solid`,
                          borderWidth: '1px',
                          mt: 0.25, // Slight offset to align with first line of text
                        })}
                      >
                        <ViewArrayOutlined fontSize="small" color="primary" />
                      </Box>
                      <Box
                        sx={{
                          minWidth: 0,
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            component="div"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {workflow.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {workflow.stages.length}{' '}
                            {workflow.stages.length === 1 ? 'stage' : 'stages'}
                          </Typography>
                        </Box>
                        {workflow.isDefault && (
                          <Chip
                            label="Default"
                            size="small"
                            variant="outlined"
                            aria-label={`${workflow.name} default workflow`}
                            sx={{
                              flexShrink: 0,
                              color: 'text.secondary',
                              borderColor: 'divider',
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Separator */}
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ height: 'auto', alignSelf: 'stretch' }}
                    />

                    {/* Middle container - Stage flow with arrows */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          flexWrap: 'wrap',
                          color: 'text.secondary',
                        }}
                      >
                        {workflow.stages
                          .sort((a, b) => a.orderIndex - b.orderIndex)
                          .map((stage, index) => (
                            <Box
                              key={stage.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Typography variant="body2">
                                {stage.name}
                              </Typography>
                              {index < workflow.stages.length - 1 && (
                                <ArrowForward sx={{ fontSize: '18px' }} />
                              )}
                            </Box>
                          ))}
                      </Box>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    {!workflow.isDefault && (
                      <Button
                        variant="text"
                        size="small"
                        aria-label={`Set ${workflow.name} as default workflow`}
                        onClick={() => handleSetDefault(workflow)}
                        disabled={setDefaultMutation.isPending}
                        sx={{
                          py: 0.5,
                          px: 1.5,
                          minWidth: 112,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Set as default
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleEdit(workflow)}
                      startIcon={<EditRounded fontSize="small" />}
                      sx={{ py: 0.5, px: 1 }}
                    >
                      Edit
                    </Button>
                    {workflows.length > 1 &&
                      (() => {
                        const usageCount = getWorkflowUsageCount(workflow);
                        const isDisabled = usageCount > 0;
                        const stageIds = workflow.stages.map((s) => s.id);

                        const tooltipContent = isDisabled ? (
                          <Box>
                            Workflows cannot be deleted when they are in use.{' '}
                            <Link
                              to="/projects"
                              search={{ workflowStageIds: stageIds }}
                              style={{
                                color: 'inherit',
                                textDecoration: 'underline',
                              }}
                            >
                              {formatNumber(usageCount)}{' '}
                              {usageCount === 1 ? 'project is' : 'projects are'}
                            </Link>{' '}
                            currently using this workflow. Before this workflow
                            can be deleted, those projects must be updated to a
                            different workflow or have their workflow disabled.
                          </Box>
                        ) : undefined;

                        return (
                          <ConditionalTooltip
                            arrow
                            tooltipText={tooltipContent}
                          >
                            <IconButton
                              size="small"
                              aria-label={`Delete ${workflow.name}`}
                              onClick={() => handleDelete(workflow)}
                              disabled={isDisabled}
                              sx={{ py: 0.5, px: 1 }}
                            >
                              <DeleteOutlineRounded
                                fontSize="small"
                                color={isDisabled ? 'disabled' : 'action'}
                              />
                            </IconButton>
                          </ConditionalTooltip>
                        );
                      })()}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      {selectedWorkflow && (
        <WorkflowEditDialog
          isOpen={editDialog.isOpen}
          onClose={() => {
            editDialog.close();
            setSelectedWorkflow(null);
          }}
          workflow={selectedWorkflow}
        />
      )}

      <WorkflowEditDialog
        isOpen={addDialog.isOpen}
        onClose={() => {
          addDialog.close();
          setSelectedWorkflow(null);
        }}
      />

      <Dialog
        fullWidth
        maxWidth="sm"
        open={deleteDialog.isOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Delete workflow</DialogTitle>
        <DialogContent dividers>
          <Typography marginBottom={1}>
            Are you sure you want to delete &quot;{workflowToDelete?.name}
            &quot;? This workflow and all its stages will be permanently
            removed.
          </Typography>
          <Typography>
            (Note: your audit log will continue to show a record of the deleted
            workflow.)
          </Typography>
          {deleteErrorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle>Cannot delete workflow</AlertTitle>
              {deleteErrorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={handleCloseDeleteDialog}>
            No, cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteMutation.isPending || !canConfirmDeleteWorkflow}
          >
            Yes, delete
          </Button>
        </DialogActions>
      </Dialog>

      <SupportSnackbar
        isOpen={showDeleteSupportSnackbar}
        onClose={() => {
          setShowDeleteSupportSnackbar(false);
          deleteMutation.reset();
        }}
        message="An error occurred while deleting the workflow."
      />
    </>
  );
}
